/**
 * EVO — moliya ekranlarining URL holati.
 *
 * `lib/procurement-search.ts` va `lib/report-search.ts` bilan bir xil uchta
 * qoida:
 *   • URL ISHONCHSIZ KIRISH — har maydon tozalanadi (`?page=-5`,
 *     `?status=DROP TABLE` ekranni buzmaydi, default'ga tushadi);
 *   • DEFAULT qiymatlar URL ga YOZILMAYDI — manzil qisqa va ulashsa bo'ladigan;
 *   • oq ro'yxat serverda TAKRORLANMAYDI (`sortBy` bu yerda tekshirilmaydi:
 *     noto'g'ri maydon 400 qaytaradi va jadval xato holatini ko'rsatadi).
 *
 * ============================================================================
 * `id` — RO'YXAT VA KARTOCHKA BITTA MANZILDA
 * ============================================================================
 * Hisob-faktura va to'lov ekranlarida kartochka ALOHIDA marshrut emas
 * (`/invoices/$id` yo'q), balki `?id=` parametri. Sabab amaliy: moliyachi
 * kuniga o'nlab hujjatni ko'zdan kechiradi va har safar ro'yxatga qaytish
 * filtrni va sahifani yo'qotardi. Bir manzilda esa ro'yxat joyida qoladi,
 * kartochka esa yonida ochiladi — va bu holat HAVOLA QILINADI.
 *
 * Xarid buyurtmasi (`/procurement/orders/$id`) boshqacha va bu qarama-qarshilik
 * emas: u yerda kartochka TAHRIRLANADIGAN ko'p bosqichli hujjat (satrlar,
 * tasdiqlash, konteynerga bog'lash) va u to'liq ekranni talab qiladi. Bu
 * yerdagi kartochka esa O'QISH uchun.
 */
import { INVOICE_STATUSES, type InvoiceStatus } from './invoices';
import { PAYMENT_STATUSES, type PaymentStatus } from './payments';
import type { SortDir } from './products';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function positiveInt(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function isoDate(value: unknown): string | undefined {
  const raw = text(value);
  return raw !== undefined && ISO_DATE.test(raw) ? raw : undefined;
}

/** URL da bayroq faqat `true` bo'lganda turadi — `?openOnly=false` ma'nosiz. */
function flag(value: unknown): true | undefined {
  return value === true || value === 'true' ? true : undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/** Sahifa, saralash, qidiruv va ochilgan kartochka — to'rt ekranda bir xil. */
interface FinanceListSearch {
  readonly page?: number;
  readonly sortBy?: string;
  readonly sortDir?: SortDir;
  readonly q?: string;
  /** Ochilgan hujjat. Yo'q bo'lsa faqat ro'yxat ko'rinadi. */
  readonly id?: number;
}

function financeList(search: Record<string, unknown>): FinanceListSearch {
  const page = positiveInt(search.page);
  const id = positiveInt(search.id);

  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(text(search.sortBy) ? { sortBy: search.sortBy as string } : {}),
    ...(search.sortDir === 'desc' ? { sortDir: 'desc' as SortDir } : {}),
    ...(text(search.q) ? { q: search.q as string } : {}),
    ...(id !== undefined ? { id } : {}),
  };
}

// ---------------------------------------------------------------------------
// Hisob-fakturalar
// ---------------------------------------------------------------------------

export interface InvoiceSearch extends FinanceListSearch {
  readonly status?: InvoiceStatus;
  readonly customerId?: number;
  readonly from?: string;
  readonly to?: string;
  readonly openOnly?: true;
  readonly overdue?: true;
}

export function parseInvoiceSearch(search: Record<string, unknown>): InvoiceSearch {
  const status = oneOf(search.status, INVOICE_STATUSES);
  const customerId = positiveInt(search.customerId);
  const from = isoDate(search.from);
  const to = isoDate(search.to);

  return {
    ...financeList(search),
    ...(status ? { status } : {}),
    ...(customerId !== undefined ? { customerId } : {}),
    ...(from !== undefined ? { from } : {}),
    ...(to !== undefined ? { to } : {}),
    ...(flag(search.openOnly) ? { openOnly: true as const } : {}),
    ...(flag(search.overdue) ? { overdue: true as const } : {}),
  };
}

// ---------------------------------------------------------------------------
// To'lovlar
// ---------------------------------------------------------------------------

export interface PaymentSearch extends FinanceListSearch {
  readonly status?: PaymentStatus;
  readonly customerId?: number;
  readonly from?: string;
  readonly to?: string;
  readonly unallocatedOnly?: true;
}

export function parsePaymentSearch(search: Record<string, unknown>): PaymentSearch {
  const status = oneOf(search.status, PAYMENT_STATUSES);
  const customerId = positiveInt(search.customerId);
  const from = isoDate(search.from);
  const to = isoDate(search.to);

  return {
    ...financeList(search),
    ...(status ? { status } : {}),
    ...(customerId !== undefined ? { customerId } : {}),
    ...(from !== undefined ? { from } : {}),
    ...(to !== undefined ? { to } : {}),
    ...(flag(search.unallocatedOnly) ? { unallocatedOnly: true as const } : {}),
  };
}

// ---------------------------------------------------------------------------
// Kredit-notalar va filial xarajatlari
// ---------------------------------------------------------------------------

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ FAQAT `id` — FILTRLAR EKRAN ICHIDA QOLADI                                ║
 * ║                                                                          ║
 * ║ Ikkala ekranda ham holat tanlagichi `useState` da yashaydi va u shundoq  ║
 * ║ qoladi. Ularni ham URL ga ko'chirish OSON bo'lardi, lekin bu ishning     ║
 * ║ savoli boshqa: «shu hujjatni och» degan manzil YO'Q edi — global qidiruv ║
 * ║ (Ctrl+K) kredit-nota raqamini topib, foydalanuvchini RO'YXATGA tashlab   ║
 * ║ ketardi va u hujjatni qo'lda qayta izlardi.                              ║
 * ║                                                                          ║
 * ║ Shuning uchun bu yerda faqat kartochkani ochadigan maydon bor. Filtrni   ║
 * ║ ham qo'shish URL shartnomasini kengaytirardi (`?status=` serverda        ║
 * ║ tekshiriladi, ekranda esa uchta holat bor) va uni tekshirmasdan          ║
 * ║ qo'shish «ishlayotgandek» ko'rinadigan parametr yasardi — aynan          ║
 * ║ `lib/search.ts` da rad etilgan xato.                                     ║
 * ║                                                                          ║
 * ║ Kartochka ID BO'YICHA alohida so'rov qiladi (`GET /credit-notes/:id`,    ║
 * ║ `GET /expenses/:id`), ro'yxatdan qidirmaydi: ro'yxat 25 tadan sahifalab  ║
 * ║ keladi va qidiruvdan kelgan hujjat birinchi sahifada BO'LMASLIGI mumkin. ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
interface OpenCardSearch {
  /** Ochilgan hujjat. Yo'q bo'lsa faqat ro'yxat ko'rinadi. */
  readonly id?: number;
}

function openCard(search: Record<string, unknown>): OpenCardSearch {
  const id = positiveInt(search.id);
  return id === undefined ? {} : { id };
}

export type CreditNoteSearch = OpenCardSearch;

export function parseCreditNoteSearch(search: Record<string, unknown>): CreditNoteSearch {
  return openCard(search);
}

/*
 * XARAJAT EKRANI HALI KO'CHIRILMAGAN.
 *
 * `parseExpenseSearch` yozilgan edi, lekin `routes/finance/expenses.tsx` hamon
 * kartochkani `useState` da saqlaydi — ya'ni tahlilchi hech kim chaqirmaydigan
 * eksport bo'lib qolardi. Ishlatilmagan eksport eng yomon turdagi qoldiq: u
 * «bu allaqachon bor» degan taassurot beradi va keyingi odam ekranni
 * ko'chirish o'rniga uni izlab vaqt yo'qotadi.
 *
 * Ko'chirish qadamlari boshqa uchtasida ko'rinadi (kredit-nota, tovar qabuli,
 * xodim): `OpenCardSearch` + `openCard()` + marshrutga `validateSearch`.
 */

// ---------------------------------------------------------------------------
// Mijoz qarzlari
// ---------------------------------------------------------------------------

/**
 * `asOf` — DAVR EMAS, HOLAT SANASI.
 *
 * `/reports/ar-aging` dagi bilan bir xil mulohaza: qarz yoshi ma'lum bir
 * KUNGA hisoblangan kesim, oraliqning yig'indisi emas.
 *
 * `customerId` — ochilgan mijoz kartochkasi (`GET /customers/:id/balance`).
 */
export interface ReceivablesSearch {
  readonly asOf?: string;
  readonly customerId?: number;
  /** Faqat muddati o'tgan qarzi borlar — kunlik undirish ro'yxati. */
  readonly overdueOnly?: true;
}

export function parseReceivablesSearch(search: Record<string, unknown>): ReceivablesSearch {
  const asOf = isoDate(search.asOf);
  const customerId = positiveInt(search.customerId);

  return {
    ...(asOf !== undefined ? { asOf } : {}),
    ...(customerId !== undefined ? { customerId } : {}),
    ...(flag(search.overdueOnly) ? { overdueOnly: true as const } : {}),
  };
}

// ---------------------------------------------------------------------------
// Ta'minotchi qarzlari
// ---------------------------------------------------------------------------

/**
 * `asOf` YO'Q va bu ongli: `GET /reports/ap-aging` hech qanday parametr
 * olmaydi (`reports.controller.ts`). Ekranda sana tanlagich chizish
 * foydalanuvchini aldardi — u sanani o'zgartirar, raqam esa o'zgarmasdi.
 */
export interface PayablesSearch {
  /** Faqat ochiq avansi borlar — «osilgan pul» ro'yxati. */
  readonly advancesOnly?: true;
}

export function parsePayablesSearch(search: Record<string, unknown>): PayablesSearch {
  return flag(search.advancesOnly) ? { advancesOnly: true } : {};
}

// ---------------------------------------------------------------------------
// Ta'minotchiga to'lov
// ---------------------------------------------------------------------------

/**
 * `/finance/supplier-payments` holati.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ `supplierId` — OCHILGAN KARTOCHKA, FILTR EMAS                            │
 * │                                                                          │
 * │ Boshqa moliya ekranlaridagi `id` bilan bir xil rol: ro'yxat joyida       │
 * │ qoladi, kartochka esa yonida ochiladi va manzil HAVOLA QILINADI          │
 * │ («Shenzhen ga to'lovni ko'r» deb yuborish mumkin).                       │
 * │                                                                          │
 * │ U serverga FILTR sifatida UZATILMAYDI: `?supplierId=` `supplier.view`    │
 * │ bilan yopilgan (`field-masking.ts`), qarzlar javobi esa sahifalanmaydi   │
 * │ — kerakli zavod allaqachon yuklangan ro'yxatda turadi.                   │
 * │                                                                          │
 * │ `asOf` YO'Q: server muddatni bugunga nisbatan hisoblaydi va «kecha       │
 * │ qancha qarz edi» degan savol bu ekranning ishi emas — u AP hisobotida    │
 * │ (`/payables`).                                                          │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export interface SupplierPaymentSearch {
  readonly q?: string;
  /** Faqat muddati o'tgan invoyslar — kunlik to'lov ro'yxati. */
  readonly overdueOnly?: true;
  readonly supplierId?: number;
}

export function parseSupplierPaymentSearch(
  search: Record<string, unknown>,
): SupplierPaymentSearch {
  const supplierId = positiveInt(search.supplierId);

  return {
    ...(text(search.q) ? { q: search.q as string } : {}),
    ...(flag(search.overdueOnly) ? { overdueOnly: true as const } : {}),
    ...(supplierId !== undefined ? { supplierId } : {}),
  };
}

// ---------------------------------------------------------------------------
// Valyuta kurslari
// ---------------------------------------------------------------------------

/** Tarix chizmasi qaysi valyuta bo'yicha chizilgani — havola qilinadi. */
export const RATE_HISTORY_CURRENCIES = ['UZS', 'CNY'] as const;
export type RateHistoryCurrency = (typeof RATE_HISTORY_CURRENCIES)[number];

export interface FxRateSearch {
  readonly currency?: RateHistoryCurrency;
}

export function parseFxRateSearch(search: Record<string, unknown>): FxRateSearch {
  const currency = oneOf(search.currency, RATE_HISTORY_CURRENCIES);
  // `UZS` — default va u URL ga yozilmaydi.
  return currency !== undefined && currency !== 'UZS' ? { currency } : {};
}
